<template>
    <div class="reset-password-page">
        <div class="reset-password-container">
            <!-- Header avec logo texte -->
            <div class="reset-password-header">
                <router-link :to="{ name: 'trips' }" class="logo-link">
                    <h1 class="app-name">MyEcoCar</h1>
                </router-link>
                <p class="reset-password-subtitle">{{ $t('recuperarContraseña') }}</p>
            </div>

            <!-- État: Email envoyé avec succès -->
            <div class="success-message" v-if="send">
                <div class="success-icon">
                    <i class="fa fa-check-circle"></i>
                </div>
                <h3 class="success-title">{{ $t('emailEnviado') }}</h3>
                <p class="success-text">
                    {{ $t('seHaEnviadoEmailIndicacionesRestablecerContrasena') }}
                </p>
                <button class="btn btn-back" @click="$router.push({ name: 'login' })">
                    {{ $t('volverInicio') }}
                </button>
            </div>

            <!-- État: Demande de réinitialisation (sans token) -->
            <div class="reset-password-form" v-else-if="!token">
                <p class="form-instruction">
                    {{ $t('ingresaEmailRecibiras') }}
                </p>

                <div class="form-group">
                    <label for="txt_email">{{ $t('email') }}</label>
                    <input
                        v-jump
                        type="email"
                        id="txt_email"
                        class="form-input"
                        v-model="email"
                        :placeholder="$t('ingresaTuEmail')"
                        :class="{ 'has-error': error }"
                    />
                    <span class="error-message" v-if="error">{{ error }}</span>
                </div>

                <button
                    v-jump
                    class="btn btn-reset"
                    @click="reset"
                    :disabled="loading"
                >
                    <span v-if="!loading">{{ $t('enviarEmail') }}</span>
                    <spinner class="spinner-white" v-if="loading"></spinner>
                </button>

                <div class="back-to-login">
                    <router-link :to="{ name: 'login' }">
                        <i class="fa fa-arrow-left"></i>
                        {{ $t('volverLogin') }}
                    </router-link>
                </div>
            </div>

            <!-- État: Changement de mot de passe (avec token) -->
            <div class="reset-password-form" v-else-if="token">
                <p class="form-instruction">
                    {{ $t('ingresaNuevoPassword') }}
                </p>

                <div class="form-group">
                    <label for="txt_password">{{ $t('nuevoPassword') }}</label>
                    <input
                        v-jump
                        type="password"
                        id="txt_password"
                        class="form-input"
                        v-model="password"
                        :placeholder="$t('ingresaNuevoPassword')"
                        :class="{ 'has-error': error }"
                    />
                </div>

                <div class="form-group">
                    <label for="txt_password_confirm">{{ $t('repetirContrasena') }}</label>
                    <input
                        v-jump
                        type="password"
                        id="txt_password_confirm"
                        class="form-input"
                        v-model="password_confirmation"
                        :placeholder="$t('confirmarPassword')"
                        :class="{ 'has-error': error }"
                    />
                </div>

                <span class="error-message" v-if="error">{{ error }}</span>

                <button
                    v-jump
                    class="btn btn-reset"
                    @click="change"
                    :disabled="loading"
                >
                    <span v-if="!loading">{{ $t('cambiarPassword') }}</span>
                    <spinner class="spinner-white" v-if="loading"></spinner>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import bus from '../../services/bus-event';
import router from '../../router';
import Spinner from '../Spinner.vue';
let emailRegex =
    /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

export default {
    name: 'reset-password',
    props: {
        token: {
            type: String,
            required: false
        }
    },

    data() {
        return {
            email: '',
            loading: false,
            error: null,
            send: false,
            password_confirmation: '',
            password: '',
            carpoolear_logo:
                process.env.ROUTE_BASE + 'static/img/carpoolear_logo.png'
        };
    },
    computed: {
        ...mapGetters({
            isMobile: 'device/isMobile',
            settings: 'auth/appConfig'
        }),
        tripCardTheme() {
            return this.settings ? this.settings.trip_card_design : '';
        }
    },

    methods: {
        ...mapActions({
            resetPassword: 'auth/resetPassword',
            changePassword: 'auth/changePassword'
        }),

        reset() {
            this.error = null;
            if (emailRegex.test(this.email)) {
                this.loading = true;
                this.resetPassword(this.email).then(
                    () => {
                        this.loading = false;
                        this.send = true;
                    },
                    () => {
                        this.loading = false;
                        this.error =
                            this.$t('emailIngresadoNoPerteneceUsuario');
                    }
                );
            } else {
                this.error = this.$t('ingreseEmailValido');
            }
        },

        change() {
            this.error = null;
            if (this.password === this.password_confirmation) {
                this.loading = true;
                let data = {};
                data.password = this.password;
                data.password_confirmation = this.password_confirmation;
                let token = this.token;
                this.changePassword({ token, data }).then(
                    () => {
                        this.$router.replace({ name: 'login' });
                    },
                    () => {
                        this.loading = false;
                        this.error = this.$t('tokenInvalido');
                    }
                );
            } else {
                this.error = this.$t('noCoincidenCampos');
            }
        },
        onBackClick() {
            router.back();
        }
    },

    mounted() {
        bus.on('back-click', this.onBackClick);
    },

    beforeDestroy() {
        bus.off('back-click', this.onBackClick);
    },

    components: {
        Spinner
    }
};
</script>

<style>
/* Force le fond vert sur toute la page de reset password */
body {
    background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%) !important;
    min-height: 100vh;
}

/* Force également sur l'app-container pour la page reset-password */
.app-container:has(.reset-password-page) {
    min-height: 100vh !important;
    background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%) !important;
}
</style>

<style scoped>
/* === Layout Principal === */
.reset-password-page {
    position: relative;
    /* height: 100vh; */
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px 20px;
    font-family: 'Heebo', 'Poppins', sans-serif;
    overflow: hidden;
}

/* Fond vert qui couvre tout */
.reset-password-page::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
    z-index: -1;
}

.reset-password-container {
    width: 100%;
    max-width: 650px;
    min-height: 15vh;
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    padding: 30px 40px;
    animation: fadeIn 0.5s ease;
    margin: 0;
    max-height: 95vh;
    overflow-y: auto;
}

/* Masquer la scrollbar tout en gardant la fonctionnalité de scroll */
.reset-password-container::-webkit-scrollbar {
    width: 0;
    display: none;
}

.reset-password-container {
    -ms-overflow-style: none;  /* IE et Edge */
    scrollbar-width: none;  /* Firefox */
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* === Header === */
.reset-password-header {
    text-align: center;
    margin-bottom: 25px;
}

.logo-link {
    text-decoration: none;
}

.app-name {
    font-family: 'Poppins', sans-serif;
    font-size: 34px;
    font-weight: 700;
    color: #59c362;
    margin: 0 0 8px 0;
    letter-spacing: -0.5px;
    transition: all 0.3s ease;
}

.logo-link:hover .app-name {
    color: #48a551;
    transform: scale(1.02);
}

.reset-password-subtitle {
    font-size: 17px;
    font-weight: 400;
    color: #666;
    margin: 0;
}

/* === Formulaire === */
.reset-password-form {
    width: 100%;
}

.form-instruction {
    text-align: center;
    color: #666;
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 25px;
}

.form-group {
    margin-bottom: 16px;
}

.form-group label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 6px;
    font-family: 'Heebo', sans-serif;
}

.form-input {
    width: 100%;
    height: 48px;
    border-radius: 10px;
    border: 2px solid #d0d0d0;
    padding: 12px 16px;
    font-size: 15px;
    font-weight: 500;
    font-family: 'Heebo', sans-serif;
    color: #333;
    background: white;
    transition: all 0.3s ease;
    box-sizing: border-box;
}

.form-input:focus {
    outline: none;
    border-color: #59c362;
    box-shadow: 0 2px 12px rgba(89, 195, 98, 0.2);
}

.form-input::placeholder {
    color: #999;
    font-weight: 400;
}

.form-input.has-error {
    border-color: #dc3545;
}

.error-message {
    display: block;
    color: #dc3545;
    font-size: 13px;
    font-weight: 500;
    margin-top: 8px;
    margin-bottom: 12px;
}

/* === Bouton de réinitialisation === */
.btn-reset {
    width: 100%;
    height: 50px;
    background: linear-gradient(135deg, #59c362 0%, #48a551 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 700;
    font-family: 'Heebo', sans-serif;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(89, 195, 98, 0.3);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
}

.btn-reset:hover:not(:disabled) {
    background: linear-gradient(135deg, #48a551 0%, #3d8f44 100%);
    box-shadow: 0 6px 16px rgba(89, 195, 98, 0.4);
    transform: translateY(-2px);
}

.btn-reset:active:not(:disabled) {
    transform: translateY(0);
}

.btn-reset:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* === Lien retour au login === */
.back-to-login {
    text-align: center;
    margin-top: 25px;
}

.back-to-login a {
    color: #59c362;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    transition: color 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.back-to-login a:hover {
    color: #48a551;
    text-decoration: underline;
}

.back-to-login i {
    font-size: 12px;
}

/* === Message de succès === */
.success-message {
    text-align: center;
    padding: 20px;
}

.success-icon {
    font-size: 64px;
    color: #59c362;
    margin-bottom: 20px;
    animation: scaleIn 0.5s ease;
}

@keyframes scaleIn {
    from {
        transform: scale(0);
        opacity: 0;
    }
    to {
        transform: scale(1);
        opacity: 1;
    }
}

.success-title {
    font-size: 24px;
    font-weight: 700;
    color: #333;
    margin: 0 0 15px 0;
    font-family: 'Poppins', sans-serif;
}

.success-text {
    font-size: 15px;
    color: #666;
    line-height: 1.6;
    margin-bottom: 30px;
}

.btn-back {
    width: 100%;
    height: 50px;
    background: white;
    color: #59c362;
    border: 2px solid #59c362;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 700;
    font-family: 'Heebo', sans-serif;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.btn-back:hover {
    background: #59c362;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(89, 195, 98, 0.3);
}

/* === Responsive === */
@media only screen and (max-width: 768px) {
    .reset-password-page {
        height: 100vh;
        padding: 10px 15px;
        overflow: hidden;
    }

    .reset-password-container {
        padding: 25px 30px;
        border-radius: 15px;
        margin: 0;
        max-height: 96vh;
    }

    .app-name {
        font-size: 30px;
    }

    .reset-password-subtitle {
        font-size: 16px;
    }

    .form-group {
        margin-bottom: 14px;
    }

    .btn-reset {
        height: 48px;
        font-size: 15px;
    }

    .success-icon {
        font-size: 56px;
    }

    .success-title {
        font-size: 22px;
    }
}

@media only screen and (max-width: 420px) {
    .reset-password-page {
        padding: 8px 12px;
        height: 100vh;
        overflow: hidden;
    }

    .reset-password-container {
        padding: 20px 25px;
        max-height: 97vh;
    }

    .app-name {
        font-size: 28px;
    }

    .success-icon {
        font-size: 48px;
    }

    .success-title {
        font-size: 20px;
    }
}

/* === Spinner === */
.spinner-white {
    color: white;
}
</style>
